import React, { useRef, useState } from "react";

import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import RemoveIcon from "@mui/icons-material/Remove";
import { IconButton, Box, Paper, Button } from "@mui/material";
import { useSelector } from "react-redux";

import ChatbotWidget from "./ChatbotWidget";
import caLogo from "../../assets/logos/ca-logo.svg";
import { ChatbotHeaderConfig } from "../../store/modules/appConfigs/appConfigsReducer";
import { RootState } from "../../store/store";

const alignmentMap: Record<string, string> = {
  left: "flex-start",
  center: "center",
  right: "flex-end",
};

const NAVBAR_HEIGHT = { xs: 64, lg: 94 };
const FOOTER_HEIGHT = { xs: 70, lg: 120 };
const FAB_SIZE = 56;
const FAB_BOTTOM = 24;
const GAP = 12;

const ChatbotButton: React.FC = () => {
  const enableChatbot = useSelector((state: RootState) => state.appConfigs.enableChatbot);
  const isChatbotWhitelisted = useSelector(
    (state: RootState) => state.customer.customer?.isChatbotWhitelisted === true
  );
  const chatbotHeader = useSelector((state: RootState) => state.appConfigs.chatbotHeader);
  const [open, setOpen] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [widgetKey, setWidgetKey] = useState(0);
  const closeChatRef = useRef<(() => void) | null>(null);

  // Guard: Don't render if feature is disabled and user is not whitelisted
  if (!enableChatbot && !isChatbotWhitelisted) {
    return null;
  }

  const handleMinimise = () => {
    setOpen(false);
  };

  const handleOpenChat = () => {
    if (!open) setHasBeenOpened(true);
    setOpen(true);
  };

  const handleCloseClick = () => {
    setShowCloseModal(true);
  };

  const handleCloseConfirm = () => {
    setShowCloseModal(false);
    setOpen(false);
    setHasBeenOpened(false);
    setWidgetKey((prev) => prev + 1);
    closeChatRef.current = null;
  };

  const handleCloseCancel = () => {
    setShowCloseModal(false);
  };

  const handleFabClick = () => {
    if (open) {
      handleMinimise();
    } else {
      handleOpenChat();
    }
  };

  return (
    <>
      {/* Floating chat popup — mounted on first open to avoid premature session start */}
      <Box
        sx={{
          position: "fixed",
          bottom: FAB_BOTTOM + FAB_SIZE + GAP,
          right: 16,
          width: { xs: "calc(100vw - 32px)", sm: 400 },
          height: 800,
          maxHeight: {
            xs: `calc(100vh - ${NAVBAR_HEIGHT.xs + FAB_BOTTOM + FAB_SIZE + GAP + 16}px)`,
            lg: `calc(100vh - ${NAVBAR_HEIGHT.lg + FAB_BOTTOM + FAB_SIZE + GAP + 16}px)`,
          },
          zIndex: 1200,
          display: "flex",
          flexDirection: "column",
          visibility: open ? "visible" : "hidden",
          opacity: open ? 1 : 0,
          transform: open ? "translateY(0)" : "translateY(16px)",
          transition: "opacity 0.2s ease, transform 0.2s ease, visibility 0.2s",
          pointerEvents: open ? "auto" : "none",
        }}
      >
        <Paper
          elevation={8}
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            borderRadius: 3,
            overflow: "hidden",
            position: "relative",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: alignmentMap[chatbotHeader.alignment] ?? "flex-start",
              px: 2,
              py: 1,
              backgroundColor: chatbotHeader.backgroundColor,
              color: "#fff",
              flexShrink: 0,
              position: "relative",
              minHeight: chatbotHeader.height,
            }}
          >
            {chatbotHeader.mode === "logo" ? (
              <Box
                component="img"
                src={caLogo}
                alt={chatbotHeader.title}
                sx={{
                  display: "block",
                  maxWidth: 160,
                  width: "100%",
                  height: 28,
                  objectFit: "contain",
                }}
              />
            ) : (
              <Box sx={{ fontWeight: "bold", fontSize: 16 }}>{chatbotHeader.title}</Box>
            )}
            <IconButton onClick={handleMinimise} size="small" sx={{ color: "#fff", position: "absolute", right: 36 }}>
              <RemoveIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={handleCloseClick} size="small" sx={{ color: "#fff", position: "absolute", right: 8 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Chatbot Widget — only mounted after user first opens the panel */}
          <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
            {hasBeenOpened && (
              <ChatbotWidget
                key={widgetKey}
                onCloseReady={(fn) => {
                  closeChatRef.current = fn;
                }}
              />
            )}
          </Box>

          {/* Close Chat Confirmation Modal — contained within the widget */}
          {showCloseModal && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                zIndex: 10,
              }}
            >
              <Paper
                elevation={4}
                sx={{
                  p: 3,
                  mx: 2,
                  maxWidth: 320,
                  width: "100%",
                  borderRadius: 2,
                  textAlign: "center",
                }}
              >
                <Box sx={{ fontWeight: "bold", fontSize: 18, mb: 1 }}>Close Chat?</Box>
                <Box sx={{ fontSize: 14, color: "text.secondary", mb: 3 }}>
                  Closing this window will end the conversation. Do you want to continue?
                </Box>
                <Box sx={{ display: "flex", justifyContent: "center", gap: 1.5 }}>
                  <Button onClick={handleCloseCancel} variant="outlined" color="inherit">
                    Cancel
                  </Button>
                  <Button onClick={handleCloseConfirm} variant="contained" color="error">
                    Close Chat
                  </Button>
                </Box>
              </Paper>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Floating Action Button */}
      <IconButton
        onClick={handleFabClick}
        sx={{
          position: "fixed",
          bottom: FAB_BOTTOM,
          right: 24,
          width: FAB_SIZE,
          height: FAB_SIZE,
          backgroundColor: "#5ce4bb",
          color: "#fff",
          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
          "&:hover": {
            backgroundColor: "#4bd4ab",
          },
          zIndex: 1201,
        }}
      >
        {open ? <RemoveIcon /> : <ChatIcon />}
      </IconButton>
    </>
  );
};

export default ChatbotButton;
